import { useCallback, useEffect, useState } from 'react';
import { UserCheck } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import purchaseOrderService, { type PurchaseOrderPerson } from '../../services/purchaseOrders';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';

/**
 * /accounts/purchase-order-users: who in the organisation can raise, send and
 * receive purchase orders. Ticking someone puts the Purchase Orders card on
 * their Dashboard. The server holds the list (`purchase_order_users`).
 *
 * It sits in Accounts, and only the Accounts Admin seat changes it: saying who
 * may commit the organisation's money is an accounts decision (Rob,
 * 2026-10-09). An Accounts User reads it. It used to be on Organisation Admin.
 */

const nameOf = (p: PurchaseOrderPerson) => [p.first_name, p.last_name].filter(Boolean).join(' ') || p.email;

function PurchaseOrderPeople() {
  const { hasAccess: isAccountsAdmin } = useModuleLicense('accounts', 'admin');
  const [people, setPeople] = useState<PurchaseOrderPerson[]>([]);
  const [chosen, setChosen] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const show = (list: PurchaseOrderPerson[]) => {
    setPeople(list);
    setChosen(new Set(list.filter((p) => p.authorised).map((p) => p.id)));
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await purchaseOrderService.getPeople();
      if (res.success && res.data) show(res.data);
      else setError(errorMessage(res, 'Could not load who is authorised'));
    } catch (e) {
      setError(errorMessage(e, 'Could not load who is authorised'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (id: number) => {
    setSaved(false);
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await purchaseOrderService.setAuthorised([...chosen]);
      if (res.success && res.data) {
        show(res.data);
        setSaved(true);
      } else {
        setError(errorMessage(res, 'Could not save who is authorised'));
      }
    } catch (e) {
      setError(errorMessage(e, 'Could not save who is authorised'));
    } finally {
      setSaving(false);
    }
  };

  const authorisedNow = people.filter((p) => p.authorised).length;
  const changed = people.some((p) => p.authorised !== chosen.has(p.id));

  return (
    <>
      <PageHeader
        title="Who raises purchase orders"
        subtitle={loading || error ? 'Choose who can raise purchase orders'
          : authorisedNow === 0 ? 'Nobody is authorised yet'
            : `${authorisedNow} ${authorisedNow === 1 ? 'person is' : 'people are'} authorised`}
        icon={<UserCheck className="h-6 w-6 text-white" />}
      />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        <p className="text-sm text-neutral-600 break-words">
          Someone ticked here gets the Purchase Orders card on their Dashboard and can raise an order for a
          farm, send it to the supplier, receive it and cancel it. Nobody else can, whatever licence seats
          they hold. Anyone with an Accounts seat can read purchase orders without being ticked.
        </p>

        {!isAccountsAdmin && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 break-words">
            You hold the Accounts User seat, so you can see this list but not change it. An Accounts Admin
            chooses who raises purchase orders.
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>
        )}

        {loading ? (
          <div className="h-24 bg-neutral-100 rounded-lg animate-pulse" />
        ) : people.length > 0 && (
          <fieldset disabled={!isAccountsAdmin} className="min-w-0">
            <ul className="divide-y divide-neutral-100 bg-white border border-neutral-200 rounded-lg">
              {people.map((p) => (
                <li key={p.id}>
                  <label className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-neutral-50">
                    <input
                      type="checkbox"
                      checked={chosen.has(p.id)}
                      onChange={() => toggle(p.id)}
                      className="mt-1 h-4 w-4 rounded border-neutral-300 text-indigo-600 shrink-0"
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-neutral-900 break-words">{nameOf(p)}</span>
                      <span className="block text-xs text-neutral-500 break-all">{p.email}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
            {isAccountsAdmin && (
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Button variant="primary" size="sm" onClick={save} disabled={!changed || saving}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
                {saved && !changed && <span className="text-sm text-green-700">Saved.</span>}
                {changed && <span className="text-sm text-neutral-500">{chosen.size} ticked, not saved yet.</span>}
              </div>
            )}
          </fieldset>
        )}
      </div>
    </>
  );
}

export default PurchaseOrderPeople;
